import { useState, useEffect, useCallback, useMemo } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getCombinedGenerators,
  getCombinedGenDataMultiple,
  getCombinedGenDataPaginated,
  getOverviewGenMetrics,  
} from "../../../Rest_api/RestApi";
import { formatDateForAPI, R_CHART_TYPES } from "../const";
import { convertToString, getMonthFromDate } from "../../../extensions/date";
import { GenerationCommon } from "../../../models/gridco";
import { BrushStart } from "../../../models/chart_model";
import { SearchBox } from "../../../components/Search";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { titleCase } from "../../../extensions/extension";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { renderHourTick } from "../../../components/recharts/components";
import { useSelector } from "react-redux";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { GridcoOverviewGenrationData } from "../../../models/metrics";
import { MetricChipHead } from "../../../components/Metrics";
const CHART_TYPES = [R_CHART_TYPES[0], R_CHART_TYPES[1], R_CHART_TYPES[2]];
const GROUP_BY = [
  { lable: "Area", value: "area" },
  { lable: "Ownership", value: "ownership" },
  { lable: "Generator Type", value: "generator_type" },
  { lable: "Station", value: "station_name" },
];
const UNITS = ["MW", "MWh", "MU", "LU"];
const BLOCKS_96 = Array.from({ length: 96 }, (_, i) => i + 1);
const DEFAULT_RECORDS = 96;

function addAreaTotals(item: any) {
  return {
    ...item,
    total_value_mw:
      (item.Interstate_value_mw || 0) + (item.Intrastate_value_mw || 0),
    total_value_mwh:
      (item.Interstate_value_mwh || 0) + (item.Intrastate_value_mwh || 0),
    total_value_mu:
      (item.Interstate_value_mu || 0) + (item.Intrastate_value_mu || 0),
    total_value_lu:
      (item.Interstate_value_lu || 0) + (item.Intrastate_value_lu || 0),
  };
}

function getChartKeys(xAxisKey: string, xAxisValues: string[]): string[] {
  if (xAxisKey === "time") return ["date", "slot"];
  if (xAxisKey === "date") return ["date"];
  return [xAxisValues[0]];
}

const INTERVAL = [
  {
    label: "Time Slot",
    key: "time",
    name: "Time Slot",
    firstKey: "slot",
    secondKey: "date",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    name: "Date",
    firstKey: "date",
    secondKey: undefined,
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    name: "Month",
    firstKey: "month",
    secondKey: undefined,
    values: ["month"],
    isTime: false,
  },
  {
    label: "Generator Type",
    key: "generator_type",
    name: "Generator Type",
    firstKey: "generator_type",
    secondKey: undefined,
    values: ["generator_type"],
    isTime: false,
  },
  {
    label: "Station",
    key: "station_name",
    name: "Station",
    firstKey: "station_name",
    secondKey: undefined,
    values: ["station_name"],
    isTime: false,
  },
];

export function GridcoGenrationOverview() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const [data, setData] = useState<GenerationCommon[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [uniqueSource, setUniqueSource] = useState<string[]>([]);
  const [selectedSource, setSelectedSource] = useState<string[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(CHART_TYPES[2]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedXaxis, setSelectedXaxis] = useState(INTERVAL[0]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[0].value);
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState<GridcoOverviewGenrationData | null>(null);
      const [metricsLoading, setMetricsLoading] = useState(false);
      
      const fetchMetrics = useCallback(async () => {
        if (!DefaultDates[activeTab]?.defaultRangeDate) {
          setMetrics(null);
          return;
        }
    
        setMetricsLoading(true);
        try {
          const dates = DefaultDates[activeTab].defaultRangeDate;
          const startDateStr = formatDateForAPI(dates.startDate);
          const endDateStr = formatDateForAPI(dates.endDate);
    
          const metricsData = await getOverviewGenMetrics({
            ranges: [
              {
                startDate: startDateStr,
                endDate: endDateStr,
                blocks: selectedBlock.map((block) => block.value),
              },
            ],
            aggregated: true,
          });
    
          setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
        } catch (error) {
          console.error("Error fetching metrics:", error);
          setMetrics(null);
        } finally {
          setMetricsLoading(false);
        }
      }, [DefaultDates, activeTab, selectedBlock]);
    
      useEffect(() => {
        fetchMetrics();
      }, [fetchMetrics]);

  const [allGenerators, setAllGenerators] = useState<{
    intrastate: string[];
    isgs: string[];
    re: string[];
  }>({ intrastate: [], isgs: [], re: [] });

  // Two separate selections; generator name == station name in this dataset
  const [selectedGenerators, setSelectedGenerators] = useState<any[]>([]);

  // Pagination state for Table tab
  const [tablePage, setTablePage] = useState(1);
  const [tableRecords, setTableRecords] = useState(DEFAULT_RECORDS);
  const [tableTotalCount, setTableTotalCount] = useState(0);
  const [tableTotalPages, setTableTotalPages] = useState(0);

  // Derive the active selection (union of both dropdowns, they reference the same names)
  const activeSelection = useMemo(
    () => selectedGenerators,
    [selectedGenerators],
  );

  // Resolve active selection into per-bucket generator lists
  const resolvedGenerators = useMemo(() => {
    const names = new Set(activeSelection.map((g) => g.value));
    return {
      intrastate: allGenerators.intrastate.filter((g) => names.has(g)),
      isgs: allGenerators.isgs.filter((g) => names.has(g)),
      re: allGenerators.re.filter((g) => names.has(g)),
    };
  }, [activeSelection, allGenerators]);

  // Fetch generator list once on date change
  const _handleFetchGenerators = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) return;
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const startDate = convertToString(dates.startDate);
    const endDate = convertToString(dates.endDate);
    const generators = await getCombinedGenerators({ startDate, endDate });
    const intrastate = generators.intrastate || [];
    const isgs = generators.isgs || [];
    const re = generators.re || [];
    setAllGenerators({ intrastate, isgs, re });

    // Auto-select the defaults (first from each bucket) so dropdowns show them
    const seen = new Set<string>();
    const defaults: { value: string; label: string }[] = [];
    [intrastate[0], isgs[0], re[0]].forEach((name) => {
      if (name && !seen.has(name)) {
        seen.add(name);
        defaults.push({ value: name, label: name });
      }
    });
    setSelectedGenerators(defaults);
    setData([]);
  }, [DefaultDates, activeTab]);

  // Fetch chart/summary data (all pages via getCombinedGenDataMultiple)
  const _handleFetchChartData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) return;
    const hasAny =
      resolvedGenerators.intrastate.length > 0 ||
      resolvedGenerators.isgs.length > 0 ||
      resolvedGenerators.re.length > 0;
    if (!hasAny) return;

    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const startDate = convertToString(dates.startDate);
    const endDate = convertToString(dates.endDate);

    const combinedData = await getCombinedGenDataMultiple({
      startDate,
      endDate,
      intrastateGenerators: resolvedGenerators.intrastate,
      isgsGenerators: resolvedGenerators.isgs,
      reGenerators: resolvedGenerators.re,
    });

    const newData: GenerationCommon[] = combinedData;
    setData(newData);
    setUniqueSource(
      Array.from(
        new Set(newData.map((item) => titleCase(item.generator_type))),
      ),
    );
    setUniqueBlock(Array.from(new Set(newData.map((item) => item.time_block))));
    setLoading(false);
  }, [DefaultDates, activeTab, resolvedGenerators]);

  // Fetch paginated table data
  const _handleFetchTableData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) return;
    const hasAny =
      resolvedGenerators.intrastate.length > 0 ||
      resolvedGenerators.isgs.length > 0 ||
      resolvedGenerators.re.length > 0;
    if (!hasAny) return;

    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const startDate = convertToString(dates.startDate);
    const endDate = convertToString(dates.endDate);

    const result = await getCombinedGenDataPaginated({
      startDate,
      endDate,
      intrastateGenerators: resolvedGenerators.intrastate,
      isgsGenerators: resolvedGenerators.isgs,
      reGenerators: resolvedGenerators.re,
      page: tablePage,
      records: tableRecords,
    });

    const total =
      result.totalRecords.intrastate +
      result.totalRecords.isgs +
      result.totalRecords.re;

    setTableTotalCount(total);
    setTableTotalPages(result.totalPages);
    setTableData(
      result.data.map((item) => ({
        ...item,
        value_mw: item.value_mw || 0,
        value_mwh: (item.value_mw || 0) / 4,
        value_mu: (item.value_mw || 0) / 4000,
        value_lu: (item.value_mw || 0) / 400,
        slot: item.time_block,
        station_name:
          item.station_name || item.sub_station || item.beneficiery_from,
      })),
    );
    setLoading(false);
  }, [DefaultDates, activeTab, resolvedGenerators, tablePage, tableRecords]);

  useEffect(() => {
    const processedData = data.map((item) => ({
      ...item,
      value_mw: item.value_mw || 0,
      value_mwh: item.value_mw / 4,
      value_mu: item.value_mw / 4000,
      value_lu: item.value_mw / 400,
      month: getMonthFromDate(item.date),
      slot: item.time_block,
      generator_type:
        item.generator_type.charAt(0).toUpperCase() +
        item.generator_type.slice(1),
      station_name:
        item.station_name || item.sub_station || item.beneficiery_from,
    }));

    let filteredData = processedData;
    console.log(filteredData);
    if (DefaultDates[activeTab].defaultRangeDate === undefined) return;

    const dates = DefaultDates[activeTab].defaultRangeDate;
    const startTime = dates.startDate.getTime();
    const endTime = dates.endDate.getTime();
    filteredData = filteredData.filter((item) => {
      const t = new Date(item.date).getTime();
      return t >= startTime && t <= endTime;
    });

    if (selectedBlock.length > 0) {
      const blockSet = new Set(selectedBlock.map((b) => b.value));
      filteredData = filteredData.filter((item) => blockSet.has(item.slot));
    }

    if (selectedSource.length > 0) {
      const sourceSet = new Set(selectedSource);
      filteredData = filteredData.filter((item) =>
        sourceSet.has(titleCase(item.generator_type)),
      );
    }

    const par: ChartDataParams = {
      keys: getChartKeys(selectedXaxis.key, selectedXaxis.values),
      tableKeys: ["date", "slot"],
      groupBy: [selectedGroupKeys],
      volumeKey: "",
      aggregationKeys: ["value_mw", "value_mwh", "value_mu", "value_lu"],
      priceKey: "",
      data: filteredData,
      averageKeys: ["value_mw"],
      averageDuplicationKeys: ["date", "slot"],
      reverseSort: ["date", "slot"],
    };

    const cData = FormatChartData(par);

    const processedChartData =
      selectedGroupKeys === "area" ? cData.data.map(addAreaTotals) : cData.data;

    setChartData(processedChartData);
    setChartKeys(cData.keys);
  }, [
    DefaultDates,
    activeTab,
    data,
    selectedBlock,
    selectedGroupKeys,
    selectedSource,
    selectedXaxis,
  ]);

  useEffect(() => {
    _handleFetchGenerators();
  }, [_handleFetchGenerators]);

  useEffect(() => {
    if (dataTab.label !== "Table") {
      _handleFetchChartData();
    }
  }, [_handleFetchChartData, dataTab.label]);

  useEffect(() => {
    if (dataTab.label === "Table") {
      _handleFetchTableData();
    }
  }, [_handleFetchTableData, dataTab.label]);

  // Reset to page 1 when generators or dates change
  useEffect(() => {
    setTablePage(1);
  }, [resolvedGenerators, DefaultDates, activeTab]);

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys(GROUP_BY[index].value);
  }

  const handleKeyChange = (index: number, setSelectedUnit: any) => {
    setSelectedUnit("MW");
    setSelectedXaxis(INTERVAL[index]);
  };

  const xDataKey = useMemo(
    () => (selectedXaxis.key === "time" ? "slot" : selectedXaxis.key),
    [selectedXaxis.key],
  );

  const secondDataKey = useMemo(
    () => (selectedXaxis.isTime ? "date" : undefined),
    [selectedXaxis.isTime],
  );

  const summaryExtraKeys = useMemo(
    () =>
      selectedXaxis.isTime
        ? ["slot", selectedXaxis.values[1]]
        : [selectedXaxis.values[0]],
    [selectedXaxis],
  );

  const summaryExtraLabels = useMemo(
    () =>
      selectedXaxis.values.map((value) => (value === "date" ? "Date" : value)),
    [selectedXaxis],
  );

  const legends = useMemo(() => {
    const unitKey = "_value_" + selectedUnit.toLowerCase();
    const baseLegends = chartKeys.map((item) => ({
      dataKey: item + unitKey,
      name: item,
      legendColor: undefined,
      type: selectedChartType.value,
      stackId: selectedChartType.stackId,
    }));
    if (selectedGroupKeys === "area") {
      return [
        ...baseLegends,
        {
          dataKey: "total" + unitKey,
          name: "Total",
          legendColor: undefined,
          type: selectedChartType.value,
        },
      ];
    }
    return baseLegends;
  }, [chartKeys, selectedUnit, selectedChartType, selectedGroupKeys]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const tablePar = useMemo(
    () => ({
      legends: visibleLegends,
      data: chartData,
      tableData: TableData,
      itemsPerPage: tableRecords,
      unit: selectedUnit,
      extraKeys: summaryExtraKeys,
      extraLabels: summaryExtraLabels,
      filename: "Generation Overview",
      serverSidePagination: dataTab.label === "Table",
      totalCount: tableTotalCount,
      serverTotalPages: tableTotalPages,
      currentServerPage: tablePage,
      onServerPageChange: (page: number) => setTablePage(page),
      onServerItemsPerPageChange: (size: number) => {
        setTableRecords(size);
        setTablePage(1);
      },
    }),
    [
      visibleLegends,
      chartData,
      TableData,
      tableRecords,
      selectedUnit,
      summaryExtraKeys,
      summaryExtraLabels,
      dataTab.label,
      tableTotalCount,
      tableTotalPages,
      tablePage,
    ],
  );

  // All unique names across all buckets (deduplicated)
  const allOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    [
      ...allGenerators.intrastate,
      ...allGenerators.isgs,
      ...allGenerators.re,
    ].forEach((g) => {
      if (!seen.has(g)) {
        seen.add(g);
        opts.push({ value: g, label: g });
      }
    });
    return opts;
  }, [allGenerators]);

  const generatorOptions = useMemo(() => allOptions, [allOptions]);

  const xTick = useMemo(
    () =>
      selectedXaxis.isTime && selectedBlock.length === 0
        ? (value: any, index: any) => renderHourTick(value, 10)
        : undefined,
    [selectedXaxis.isTime, selectedBlock.length],
  );

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X Axis"
            items={INTERVAL.map((item) => item.name)}
            selectedItems={[selectedXaxis.name]}
            onSelect={(item, index) => {
              handleKeyChange(index, setSelectedUnit);
            }}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              setSelectedUnit(item);
            }}
          />

          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.lable)}
            selectedItems={[
              GROUP_BY.find((g) => g.value === selectedGroupKeys)?.lable || "",
            ]}
            onSelect={(item, index) => {
              _handleGroupByChange(index);
            }}
          />

          <FilterMenu
            title="Generator Type"
            items={uniqueSource}
            selectedItems={selectedSource}
            setSelectedItems={setSelectedSource}
          />

          <FilterMenu
            title="Chart Type"
            items={CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              title="Thermal Generation"
              items={[
                {
                  label: "Total",
                  value:
                    metrics.thermal_generation["Total Thermal Generation (MU)"]
                      .value,
                  unit: metrics.thermal_generation[
                    "Total Thermal Generation (MU)"
                  ].unit,
                },
                {
                  label: "Peak",
                  value:
                    metrics.thermal_generation["Peak Thermal Generation (MW)"]
                      .value,
                  unit: metrics.thermal_generation[
                    "Peak Thermal Generation (MW)"
                  ].unit,
                },
              ]}
            />
            <MetricChipHead
              title="RE Generation"
              items={[
                {
                  label: "Total",
                  value:
                    metrics.re_generation["Total RE Generation (MU)"].value,
                  unit: metrics.re_generation["Total RE Generation (MU)"].unit,
                },
                {
                  label: "Peak",
                  value: metrics.re_generation["Peak RE Generation (MW)"].value,
                  unit: metrics.re_generation["Peak RE Generation (MW)"].unit,
                },
              ]}
            />
            <MetricChipHead
              title="Percentage Share"
              items={[
                {
                  label: "Thermal",
                  value: metrics.percentage_share["Thermal % Share"].value,
                  unit: metrics.percentage_share["Thermal % Share"].unit,
                },
                {
                  label: "RE",
                  value: metrics.percentage_share["RE % Share"].value,
                  unit: metrics.percentage_share["RE % Share"].unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Generation Overview"
              subtitle="Fetching Gridco combined generation data…"
            />
          ) : (
            <>
              <div className="gas-heading">
                <h2 className="chartHeading w-80">Generation Overview</h2>
                <div className="flex gap-x-2 w-full justify-end">
                  <SearchBox
                    options={generatorOptions}
                    isMany={true}
                    placeholder="Search Generator"
                    selectedOptions={selectedGenerators}
                    onChange={setSelectedGenerators}
                  />
                  <BlockRangeSelector
                    blocks={BLOCKS_96}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV(tablePar);
                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(tablePar);
                      dataTab.label === "Table" &&
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  xAxisTickMargin={10}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedXaxis.firstKey === "slot"}
                  onlyTitle={true}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends}
                  defaultSelectedLegends={selectedLegends}
                  onLegendSelect={handleLegendSelect}
                  xDataKey={xDataKey}
                  xTick={xTick}
                  secondXDataKey={secondDataKey}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={
                <p>
                  1. SLDC: Daily CGP scheduling <br /> 2.ERLDC: Daily final
                  scheduling <br /> 3. ABT Meter readings (W exp) :
                  Solar/SHEP/Biomass. <br /> 4. Gridco : Vedanta IPP, OPGC Stage
                  II, State Hydro.
                </p>
              }
              text="1. All scheduling to Odisha Solar beneficiary is considered hybrid power.
              2. OPGC Stage I taken from state hydro file (IB Unit I & II).
              3. Solar/SHEP/Biomass has been named mapped.
              4. OPGC Stage II Entitlement is taken from Declared Capacity.
              5. Hydro Entitlement is same as Expp.
              6. Vedanta Entitlement is taken from Total Injection (considering backdown).
              7. Wind power generation is taken from ERLDC (GNA & LTA).

"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
